/**
 * Confirmed via console.trace: Rebuy's own onsite script
 * (cdn.rebuyengine.com/onsite/js/global-*.js) sets `inert` on rendered widgets
 * and doesn't clear it on the visible instance, leaving it permanently
 * non-interactive. It correctly marks genuinely-hidden duplicate instances
 * (Rebuy's own `is-hidden` class, e.g. the mobile/desktop pair some widgets
 * render) as inert - that's left alone below. Only the wrongly-inert *visible*
 * instances are blocked/stripped here while this is raised with Rebuy support.
 * Remove once resolved.
 */

const REBUY_SELECTOR = '.rebuy-widget, [data-rebuy-id]';

/** @param {Element} el */
function isRebuyElement(el) {
  return el instanceof Element && (el.matches(REBUY_SELECTOR) || el.closest(REBUY_SELECTOR) !== null);
}

/**
 * @param {Element} el
 * @returns {boolean} true if el is a rebuy widget Rebuy itself marked hidden
 */
function isRebuyHiddenInstance(el) {
  const widget = el.matches('.rebuy-widget') ? el : el.closest('.rebuy-widget');
  return widget?.classList.contains('is-hidden') ?? false;
}

/**
 * Pulls the first stack frame that isn't this file, so the offending script's
 * URL/line surfaces directly instead of requiring the trace to be expanded.
 * @returns {string}
 */
function findCaller() {
  const lines = new Error().stack?.split('\n').slice(1) ?? [];
  const frame = lines.find((line) => !line.includes('rebuy-inert-fix.js'));
  return frame?.trim() ?? '(unknown - could not parse stack)';
}

/** @param {Element} el @param {string} source */
function logBlocked(el, source) {
  console.warn('[rebuy-inert-fix] blocked inert on rebuy element via', source, '\ncalled from:', findCaller(), '\nelement:', el);
  console.trace('[rebuy-inert-fix] full call stack');
}

const originalSetAttribute = Element.prototype.setAttribute;
Element.prototype.setAttribute = function (name, value) {
  if (name === 'inert' && isRebuyElement(this) && !isRebuyHiddenInstance(this)) {
    logBlocked(this, 'setAttribute');
    return;
  }
  return originalSetAttribute.call(this, name, value);
};

const originalToggleAttribute = Element.prototype.toggleAttribute;
Element.prototype.toggleAttribute = function (name, force) {
  if (name === 'inert' && isRebuyElement(this) && !isRebuyHiddenInstance(this)) {
    const wouldAdd = force === true || (force === undefined && !this.hasAttribute('inert'));
    if (wouldAdd) {
      logBlocked(this, 'toggleAttribute');
      return this.hasAttribute('inert');
    }
  }
  return originalToggleAttribute.call(this, name, force);
};

const inertDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'inert');
if (inertDescriptor?.set && inertDescriptor?.get) {
  Object.defineProperty(HTMLElement.prototype, 'inert', {
    ...inertDescriptor,
    set(value) {
      if (value && isRebuyElement(this) && !isRebuyHiddenInstance(this)) {
        logBlocked(this, 'inert property setter');
        return;
      }
      inertDescriptor.set.call(this, value);
    },
  });
}

// Safety net for anything that slips past the patches above (e.g. markup
// inserted with `inert` already present via innerHTML/insertAdjacentHTML).
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const target = mutation.target;
    if (
      target instanceof Element &&
      target.hasAttribute('inert') &&
      isRebuyElement(target) &&
      !isRebuyHiddenInstance(target)
    ) {
      console.warn('[rebuy-inert-fix] removed inert via MutationObserver safety net:', target);
      target.removeAttribute('inert');
    }
  }
});

observer.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['inert'],
  subtree: true,
});
